import { forwardRef } from 'react'

import { cn } from '@/lib/utils'
import { FxIcon } from './FxIcon'
import { FxAvatar } from './FxAvatar'

/**
 * Who a template is shared with: a `share` glyph, up to two overlapping 24px
 * avatars and "+N" for the rest.
 *
 * Initials overlapping each other are a picture, not a list — so the visuals
 * are hidden from the accessibility tree and one sentence stands in for them.
 * Without it a screen reader would read a string of loose letter pairs.
 */
export interface FxAvatarStackProps extends Omit<React.HTMLAttributes<HTMLSpanElement>, 'children'> {
  /** Initials, in the order they should overlap. */
  people: string[]
  /** How many avatars to draw before collapsing into "+N". */
  max?: number
}

export const FxAvatarStack = forwardRef<HTMLSpanElement, FxAvatarStackProps>(
  ({ people, max = 2, className, ...props }, ref) => {
    const shown = people.slice(0, max)
    const more = people.length - shown.length
    return (
      <span ref={ref} className={cn('inline-flex items-center gap-2', className)} {...props}>
        <FxIcon name="share" size={20} className="text-text-muted" />
        <span className="inline-flex" aria-hidden="true">
          {shown.map((initials, i) => (
            <FxAvatar key={`${initials}-${i}`} initials={initials} size={24} className={cn(i > 0 && '-ml-2')} />
          ))}
        </span>
        {more > 0 && (
          <span className="text-body" aria-hidden="true">
            +{more}
          </span>
        )}
        <span className="sr-only">
          {people.length === 1 ? 'Geteilt mit einer Person' : `Geteilt mit ${people.length} Personen`}
        </span>
      </span>
    )
  },
)
FxAvatarStack.displayName = 'FxAvatarStack'
